<template>
  <v-container v-if="loaded" fill-height fluid grid-list-xl>
    <!-- Audit trail dialog -->
    <v-dialog v-model="openAuditTrail" max-width="1200px">
      <app-audit-trail-timeline
        entity="lost"
        v-if="openAuditTrail"
        :entity_id="lost_id"
        :fields="config.fields"
      />
    </v-dialog>

    <v-flex>
      <!-- ═══════════════════════════════════════════════════════════════════
           SEZIONE 1 – INTESTAZIONE
           ════════════════════════════════════════════════════════════════ -->
      <app-upsert
        ref="upsert"
        :config="config"
        :item="lost"
        :title="`Lost – ${(lost.protocol && lost.protocol.name) || ''}`"
        :subtitle="lost.lost_date"
        :hide-save="true"
      >
        <!-- Pulsanti in alto a destra -->
        <template slot="headerActions">
          <app-audit-trail-btn @click="openAuditTrail = true" />

          <v-btn color="red" @click="$router.push('/lost')">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
        </template>

        <!-- Azioni principali + sezione drugs -->
        <template v-slot:actions="{ item }">
          <!-- REPLACE FILE -->
          <v-btn
            v-if="isEmnUser && lost.id"
            class="mx-1 font-weight-light"
            color="primary"
            :loading="loading"
            :disabled="loading || !item._filename"
            @click="saveNewFile(item)"
          >
            REPLACE FILE
          </v-btn>

          <!-- Download del file caricato -->
          <v-btn
            v-if="lost.filename"
            class="mx-1"
            color="purple darken-2 white--text"
            :href="`${BASE_URL}/pdf/${encodeURIComponent(lost.filename)}`"
            target="_blank"
          >
            FILE
            <v-icon right>mdi-file-download</v-icon>
          </v-btn>

          <!-- UPDATE intestazione -->
          <v-btn
            v-if="isEmnUser"
            class="mx-1 font-weight-light"
            color="primary"
            :loading="loading"
            :disabled="loading"
            @click="updateHeader(item)"
          >
            UPDATE
          </v-btn>

          <!-- ═══════════════════════════════════════════════════════════════
               SEZIONE 2 – DRUGS
               Abilitata solo dopo il primo salvataggio (lost.id esiste)
               ═══════════════════════════════════════════════════════════ -->
               
          <v-card class="mt-4 pa-2" width="100%">
            <app-table
              v-if="lost.id"
              :title="'Drugs lost'"
              :config="drugConfig"
              upsert-max-width="750px"
              @saved="drugSaved"
              @loaded="drugsLoaded"
              :filters="{ lost_id }"
              no-search
              :hidden-fields="{
                lost_id,
                type: lost.type,
                type_id: lost.type_id,
                protocol_id: lost.protocol_id,
                site_id: lost.site_id,
              }"
            />
            <v-alert
              v-if="!lost.id"
              type="info"
              dense
              border="left"
              class="ma-2"
            >
              To add lost drugs save a new item first
            </v-alert>
          </v-card>
        </template>
      </app-upsert>
    </v-flex>
  </v-container>
</template>

<script>
import config from "./config_upsert.js";
import drugConfig from "./drug_index.js";
const { getDetail, upsert } = require("@/api/base_entity");
import request from "@/utils/request";
import { mapState } from "vuex";

const BASE_URL = process.env.VUE_APP_BASE_API;

export default {
  name: "LostUpsert",

  data() {
    return {
      BASE_URL,
      config,
      drugConfig,
      lost_id: null,
      lost: {},
      loaded: false,
      loading: false,
      nDrugs: 0,
      openAuditTrail: false,
      batchSerialWarningShown: false,
    };
  },

  async created() {
    this.lost_id = Number(this.$route.params.lostId);
    this.lost = await getDetail("lost", { id: this.lost_id });
    this.loaded = true;
  },

  computed: {
    ...mapState(["user"]),
    isEmnUser() {
      return (
        this.user.role === "Administrator" || this.user.role === "EMN User"
      );
    },
  },

  methods: {
    /**
     * Salva le modifiche all'intestazione (lost_date, protocol, site, type, type_id, filename).
     * Viene chiamato dal pulsante UPDATE.
     */
    async updateHeader(item) {
      if (!this.$refs.upsert.validate()) return;
      this.loading = true;
      try {
        const formData = new FormData();
        formData.append("lost_date", item.lost_date);
        formData.append("protocol_id", item.protocol_id);
        formData.append("site_id", item.site_id);
        formData.append("type", item.type);
        formData.append("type_id", item.type_id);
        if (item.filename instanceof File) {
          formData.append("filename", item.filename);
        }
        await upsert(`lost`, formData, this.lost_id);
        this.$root.$emit("openSnackbar", {
          message: "Record updated.",
          color: "success",
        });
        // ricarica per aggiornare i dati mostrati
        this.lost = await getDetail("lost", { id: this.lost_id });
      } catch (e) {
        this.$root.$emit("openSnackbar", {
          message:
            "Error: " +
            (e?.response?.data?.message || e),
          color: "error",
        });
      }
      this.loading = false;
    },

    /**
     * Sostituisce il file salvato.
     * Chiama l'endpoint POST /lost/save_new_file/{id}
     */
    async saveNewFile(item) {
      this.loading = true;
      const formData = new FormData();
      formData.append("filename", item["_filename"]);
      try {
        await request({
          url: `/lost/save_new_file/${this.lost_id}`,
          method: "post",
          data: formData,
        });
        this.$router.go();
      } catch (e) {
        this.$root.$emit("openSnackbar", {
          message:
            "Errore: " +
            (e?.response?.data?.message || e),
          color: "error",
        });
        this.loading = false;
      }
    },

    /** Callback: il drug è stato salvato (aggiunto / modificato / rimosso). */
    drugSaved({ item }) {
      if (!item) return;
      // Mostra l'avviso se la req/ret non aveva lotto/serial per questo farmaco:
      // il dato inserito dall'utente non può essere verificato dal sistema.
      // Il flag _ref_has_no_batch_serial è impostato in drug_index.js al momento
      // della selezione del farmaco nella modale.
      if (item._ref_has_no_batch_serial) {
        this.showBatchSerialWarning();
      }
    },

    /** Callback: la tabella dei drugs ha caricato i dati. */
    drugsLoaded(items) {
      this.nDrugs = items.length;
      // Per type='request' la ref (siterequest_has_drug) non ha mai lotto né serial:
      // qualsiasi valore inserito dall'utente non può essere verificato dal sistema.
      // Mostra il banner se almeno un drug ha lotto o serials valorizzato.

      // Per type='return': il flag viene impostato al momento del salvataggio (drugSaved),
      // perché solo allora sappiamo se la ref aveva lotto/serial per quel farmaco.
    },

    showBatchSerialWarning() {
      if (this.batchSerialWarningShown) return;
      this.batchSerialWarningShown = true;
      this.$root.$emit("openSnackbar", {
        color: "warning",
        message:
          "No batch and/or serial number available. Please make sure the data inserted is correct.",
      });
    },
  },
};
</script>
